/**
 * @module ReducerManager
 *
 * @desc
 * This file is used to create a reducer manager which can dynamically add and remove reducers
 * from the redux store. @see [Documentation]{@link https://redux.js.org/usage/code-splitting#using-a-reducer-manager} for more information
 *
 *
 *
 */
import { combineReducers } from 'redux';

/**
 *
 * @param {Object} initialReducers Map of initial reducers to add to the store.
 * @return {
 * 		{
 * 			reduce: (function(*, *): EmptyObject),
 * 			add: add,
 * 			getReducerMap: (function(): *),
 * 			remove: remove
 * 		}
 * 	}
 */
export function createReducerManager(initialReducers) {
	// Create an object which maps keys to reducers
	const reducers = { ...initialReducers };

	// Create the initial combinedReducer
	let combinedReducer = combineReducers(reducers);

	// An array which is used to delete state keys when reducers are removed
	let keysToRemove = [];

	return {
		/**
		 * Returns active reducers
		 * @return {{}}
		 */
		getReducerMap: () => reducers,

		/**
		 * Root reducer function, Pass this to createStore.
		 *
		 * @example
		 * import {createStore} from 'redux';
		 * const reducerManager = createReducerManager(initialReducers);
		 * const store = createStore(reducerManager.reduce);
		 * @param state redux state
		 * @param action redux action
		 * @return Function
		 */
		reduce: (state, action) => {
			// If any reducers have been removed, clean up their state first
			if (keysToRemove.length > 0) {
				state = { ...state };
				for (let key of keysToRemove) {
					delete state[key];
				}
				keysToRemove = [];
			}

			// Delegate to the combined reducer
			return combinedReducer(state, action);
		},

		/**
		 * Adds a new reducer with the specified key to the store.
		 * @param {String} key The key name for the reducer
		 * @param {(Object|Function)} reducer The reducer object
		 */
		add: (key, reducer) => {
			if (!key || reducers[key]) {
				return;
			}

			// Add the reducer to the reducer mapping
			reducers[key] = reducer;

			// Generate a new combined reducer
			combinedReducer = combineReducers(reducers);
		},

		/**
		 * Removes a reducer with the specified key
		 * @param {String} key The key to remove
		 */
		remove: (key) => {
			if (!key || !reducers[key]) {
				return;
			}

			// Remove it from the reducer mapping
			delete reducers[key];

			// Add the key to the list of keys to clean up
			keysToRemove.push(key);

			// Generate a new combined reducer
			combinedReducer = combineReducers(reducers);
		},
	};
}
